/**
 * The web onboarding flow definition — the same flow identity, screens, and
 * navigation as the bundled mobile definition, minus the two mobile-only
 * steps:
 *
 *   - `sona_intro` (the Meet Sona screen) sits mid-flow on mobile and owns
 *     the outbound routing into the per-programme usage-baseline screens; the
 *     web flow splices it out, so its inbound edges (`change_history`,
 *     `cutback_degree`) take over those outbound rules, conditions and all.
 *   - `rewards_intro` (the rewards screen) has no outbound edges in this
 *     shared navigation (its edge into talk-or-text is mobile-only), so the
 *     web flow drops inbound rewards edges. Final question steps complete in
 *     place; conditional terminal edges retain their explicit conditions.
 *
 * Everything else is derived from the shared data in `./screens` rather than
 * re-authored, so a mobile flow edit propagates here (and the package's parity
 * tests pin the delta to exactly the spliced edges).
 */

import type { FlowDefinition, NavigationRule } from '@galapagos/shared';
import { ONBOARDING_FLOW_ID, ONBOARDING_FLOW_VERSION } from '../flow-id';
import {
  onboardingNavigation,
  onboardingProgressStepKeys,
  onboardingProgressSections,
  onboardingScreens,
} from './screens';

/** The mobile-only steps absent from the web composition. */
export const WEB_EXCLUDED_STEP_KEYS: ReadonlySet<string> = new Set(['sona_intro', 'rewards_intro']);

/**
 * Re-home an outbound rule of an excluded step onto the edge that led into it.
 * The inbound edge's own condition (if any) is merged ahead of the outbound
 * rule's, so both must hold; a key present in both would let one silently
 * override the other, which is a composition error rather than a merge.
 */
function rehome(inbound: NavigationRule, outbound: NavigationRule): NavigationRule {
  const inboundKeys = Object.keys(inbound.when ?? {});
  const outboundKeys = new Set(Object.keys(outbound.when ?? {}));
  const conflicts = inboundKeys.filter((key) => outboundKeys.has(key));
  if (conflicts.length > 0) {
    throw new Error(
      `web onboarding flow: conflicting condition keys splicing ${inbound.from_step_key} -> ${inbound.target_step_key} -> ${outbound.target_step_key}: ${conflicts.join(', ')}`,
    );
  }
  const rule: NavigationRule = {
    from_step_key: inbound.from_step_key,
    target_step_key: outbound.target_step_key,
  };
  if (inbound.when || outbound.when) {
    rule.when = { ...inbound.when, ...outbound.when };
  }
  return rule;
}

/**
 * Splice the excluded steps out of a navigation: an edge out of an excluded
 * step is dropped; an edge into an excluded step that has outbound rules is
 * replaced, in place, by those rules re-homed onto the edge's source; an edge
 * into an excluded step with no outbound rules (a terminal native step) is
 * dropped. Rule order is otherwise preserved, so first-match-wins evaluation
 * is unchanged.
 */
export function spliceExcludedSteps(
  navigation: NavigationRule[],
  excluded: ReadonlySet<string>,
): NavigationRule[] {
  return navigation.flatMap((rule) => {
    if (excluded.has(rule.from_step_key)) return [];
    if (!excluded.has(rule.target_step_key)) return [rule];
    const outbound = navigation.filter(
      (candidate) => candidate.from_step_key === rule.target_step_key,
    );
    // Single pass by design: an excluded step routing straight into another
    // excluded step would need a second splice, so it is refused rather than
    // silently re-homed onto a step the web flow does not render.
    const adjacent = outbound.find((candidate) => excluded.has(candidate.target_step_key));
    if (adjacent) {
      throw new Error(
        `web onboarding flow: adjacent excluded steps are not supported: ${rule.target_step_key} -> ${adjacent.target_step_key}`,
      );
    }
    return outbound.map((candidate) => rehome(rule, candidate));
  });
}

/** The shared navigation with the native-only steps spliced out. */
export const webOnboardingNavigation: NavigationRule[] = spliceExcludedSteps(
  onboardingNavigation,
  WEB_EXCLUDED_STEP_KEYS,
);

// The progress arrays carry neither native step (the sona_intro story screen
// keeps its own story stepper on mobile and rewards_intro is a framing screen),
// so the web composition shares them verbatim.
export const webOnboardingFlowDefinition: FlowDefinition = {
  schema_version: '1.0',
  flow_id: ONBOARDING_FLOW_ID,
  flow_version: ONBOARDING_FLOW_VERSION,
  locale: 'en',
  label: 'Onboarding',
  initial_step_key: 'start',
  progress_step_keys: onboardingProgressStepKeys,
  progress_sections: onboardingProgressSections,
  screens: Object.fromEntries(
    Object.entries(onboardingScreens).map(([stepKey, screen]) => {
      const terminalEdge = onboardingNavigation.find(
        (rule) =>
          rule.from_step_key === stepKey && rule.target_step_key === 'rewards_intro' && rule.when,
      );
      return [
        stepKey,
        terminalEdge?.when ? { ...screen, completion_when: terminalEdge.when } : screen,
      ];
    }),
  ),
  navigation: webOnboardingNavigation,
  fallback: { type: 'host' },
};
